// Inertia reconstructs history state on replacement, so retain only a small,
// passive marker alongside its own data. Never wrap native/non-Inertia state.
const TRACKER = Symbol.for('bayhive.admin.history.tracker');
const MARKER = '__bayhiveAdminHistory';
const identity = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
const inertiaState = (state) => state && typeof state === 'object' && Object.hasOwn(state, 'page');
const marker = (state) => {
  const value = state?.[MARKER];
  return value &&
    typeof value.session === 'string' &&
    typeof value.entry === 'string' &&
    Number.isInteger(value.position)
    ? value
    : null;
};

/** Install before Inertia starts, including the ordinary shop's origin entry. */
export function installAdminHistoryTracker() {
  if (window[TRACKER]) return window[TRACKER];
  const originalPush = window.history.pushState.bind(window.history);
  const originalReplace = window.history.replaceState.bind(window.history);
  const listeners = new Set();
  let suspended = false;
  const snapshot = () => {
    const entry = window.navigation?.currentEntry;
    return {
      url: window.location.href,
      state: window.history.state,
      marker: marker(window.history.state),
      index: Number.isInteger(entry?.index) && entry.index >= 0 ? entry.index : null,
      key: entry?.key || null,
    };
  };
  const update = (method, data, unused, url) => {
    if (inertiaState(data)) {
      // A blocked traversal has changed the physical history entry while the
      // editor is retained. Do not let scroll/remember overwrite that entry.
      if (suspended) return;
      const previous = marker(window.history.state);
      const next = previous
        ? {
            ...previous,
            ...(method === originalPush
              ? { position: previous.position + 1, entry: identity() }
              : {}),
          }
        : { session: identity(), position: 0, entry: identity() };
      data = { ...data, [MARKER]: next };
    }
    method(data, unused, url);
    for (const listener of listeners) listener(snapshot());
  };
  window.history.pushState = (data, unused, url) => update(originalPush, data, unused, url);
  window.history.replaceState = (data, unused, url) => update(originalReplace, data, unused, url);
  const tracker = {
    snapshot,
    suspend: (value) => {
      suspended = value;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    same(a, b) {
      if (a?.key && b?.key) return a.key === b.key;
      if (a?.marker && b?.marker)
        return a.marker.session === b.marker.session && a.marker.entry === b.marker.entry;
      return a?.index != null && b?.index != null && a.index === b.index;
    },
    delta(from, to) {
      if (from?.index != null && to?.index != null) return to.index - from.index;
      if (from?.marker && to?.marker && from.marker.session === to.marker.session)
        return to.marker.position - from.marker.position;
      return null;
    },
  };
  window[TRACKER] = tracker;
  if (inertiaState(window.history.state))
    window.history.replaceState(window.history.state, '', window.location.href);
  return tracker;
}

/**
 * Capture before Inertia's popstate listener: restore the real editor entry,
 * settle its work, then traverse again. This adds no history entries and does
 * not replace the destination's page state with the editor's page state.
 */
export function protectAdminHistory({ active, shouldGuard, settle, busy, setBusy, onBlocked }) {
  const tracker = installAdminHistoryTracker();
  let origin = tracker.snapshot();
  let transaction = null;
  let blocked = null;
  let replay = false;
  let alive = true;
  let timer = null;
  let restoration = Promise.resolve(true);
  let resolveRestoration = null;

  const finishRestoration = (ok) => {
    clearTimeout(timer);
    timer = null;
    resolveRestoration?.(ok);
    resolveRestoration = null;
  };
  const clear = () => {
    finishRestoration(true);
    tracker.suspend(false);
    if (transaction?.ownsLock) setBusy(false);
    transaction = null;
    blocked = null;
    onBlocked(false);
  };
  const failClosed = (destination) => {
    finishRestoration(false);
    if (transaction?.ownsLock) setBusy(false);
    transaction = null;
    blocked = destination;
    tracker.suspend(true);
    onBlocked(true);
  };
  const restore = (current) => {
    const delta = tracker.delta(current, origin);
    if (delta == null || delta === 0) {
      failClosed(current);
      return;
    }
    tracker.suspend(true);
    transaction.restoring = true;
    clearTimeout(timer);
    if (!resolveRestoration)
      restoration = new Promise((resolve) => {
        resolveRestoration = resolve;
      });
    window.history.go(delta);
    // A missing/disposed entry must retain the editor instead of leaving the
    // workspace permanently busy or guessing a different history direction.
    timer = setTimeout(() => failClosed(tracker.snapshot()), 2000);
  };
  const proceed = async (currentTransaction) => {
    let allowed = false;
    try {
      allowed = await settle();
      while (alive && transaction === currentTransaction && currentTransaction.restoring) {
        if (!(await restoration)) return;
      }
      if (!alive || transaction !== currentTransaction) return;
      if (!allowed) {
        clear();
        return;
      }
      const current = tracker.snapshot();
      const delta = tracker.delta(current, currentTransaction.destination);
      if (delta == null) {
        failClosed(current);
        return;
      }
      if (!delta) {
        clear();
        return;
      }
      currentTransaction.phase = 'replay';
      tracker.suspend(false);
      window.history.go(delta);
      timer = setTimeout(() => failClosed(tracker.snapshot()), 2000);
    } catch {
      if (alive && transaction === currentTransaction) failClosed(tracker.snapshot());
    }
  };
  const popstate = (event) => {
    const current = tracker.snapshot();
    if (replay) {
      replay = false;
      origin = current;
      return;
    }
    if (transaction?.phase === 'replay') {
      clear();
      origin = current;
      return;
    }
    if (transaction || blocked) {
      event.stopImmediatePropagation();
      if (tracker.same(current, origin)) {
        tracker.suspend(false);
        finishRestoration(true);
        if (blocked) {
          clear();
          return;
        }
        transaction.restoring = false;
        if (!transaction.ownsLock) {
          clear();
          return;
        }
        if (transaction.phase !== 'settling') {
          transaction.phase = 'settling';
          void proceed(transaction);
        }
      } else {
        if (blocked) {
          transaction = {
            destination: current,
            ownsLock: !busy(),
            phase: 'restoring',
            restoring: true,
          };
          if (transaction.ownsLock) setBusy(true);
          blocked = null;
          onBlocked(false);
        } else transaction.destination = current;
        restore(current);
      }
      return;
    }
    if (!active() || (!shouldGuard() && !busy())) {
      origin = current;
      return;
    }
    event.stopImmediatePropagation();
    if (tracker.same(current, origin)) return;
    transaction = { destination: current, ownsLock: !busy(), phase: 'restoring', restoring: true };
    if (transaction.ownsLock) setBusy(true);
    restore(current);
  };
  const unsubscribe = tracker.subscribe((current) => {
    if (!transaction && !blocked) origin = current;
  });
  window.addEventListener('popstate', popstate, true);
  return {
    isBlocked: () => Boolean(blocked),
    async whenRestored() {
      while (transaction?.restoring) if (!(await restoration)) return false;
      return !blocked;
    },
    async continueNavigation() {
      if (!blocked || busy()) return;
      const destination = blocked;
      setBusy(true);
      try {
        if (!(await settle()) || !alive || blocked !== destination) return;
        const current = tracker.snapshot();
        clear();
        origin = current;
        // Unstamped older sessions cannot be restored safely without the
        // Navigation API. Explicitly continue their already-selected entry.
        if (!inertiaState(current.state)) {
          window.location.reload();
          return;
        }
        replay = true;
        window.dispatchEvent(new PopStateEvent('popstate', { state: current.state }));
      } finally {
        if (alive) setBusy(false);
      }
    },
    stop() {
      alive = false;
      clear();
      unsubscribe();
      window.removeEventListener('popstate', popstate, true);
    },
  };
}
